/**
 * HTML helpers for the /docs renderers (template strings → innerHTML).
 * Every server-authored string passes through `esc()`; every URL through
 * `isHttpUrl()` before it becomes an href.
 */

const HTML_ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' };

export function esc(value: string | number | null | undefined): string {
  return String(value ?? '').replace(/[&<>"]/g, (c) => HTML_ESCAPES[c] ?? c);
}

/** Only http(s) URLs ever become links (a `javascript:` doc URL is dropped). */
export function isHttpUrl(u: string | null | undefined): u is string {
  return typeof u === 'string' && /^https?:\/\//i.test(u);
}

/** ` name="value"` when value is set, else nothing — for optional attributes. */
export function attr(name: string, value: string | null | undefined): string {
  return value ? ` ${name}="${esc(value)}"` : '';
}

/**
 * Plain text → escaped `<p>`s: a blank line starts a new paragraph, a single
 * newline stays inside one (the browser collapses it to a space). That is
 * the whole formatting vocabulary of a description (Teo 2026-09-08) — no
 * markup, no links. Empty input renders nothing.
 */
export function paragraphs(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(/\r\n?/g, '\n')
    .split(/\n[ \t]*\n/)
    .map((p) => p.trim())
    .filter((p) => p !== '')
    .map((p) => `<p>${esc(p)}</p>`)
    .join('');
}

const ICON_ATTRS =
  'width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"';
/** External-link glyph appended to links that leave the page. */
export const ICON_EXTERNAL = `<svg class="docs-ext" ${ICON_ATTRS}><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>`;
/** Tree-nav chevron (points right; CSS rotates it when the folder is open). */
export const ICON_CHEVRON = `<svg class="docs-nav-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg>`;
export const ICON_SEARCH = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>`;
